/**
 * The `form` directive renderer — the wizard's `ask_user` step. Builds controlled
 * state from the directive's fields, validates required/range constraints on
 * submit, and echoes the serialized values back to the waiting run. Every control
 * is a `@4f/ui` primitive (Form/TextInput/Select/Toggle) — no bespoke inputs.
 */
import type { UiDirective, UiFormField } from '@4f/shared';
import { Button, Form, Select, type SelectOption, Textarea, TextInput, Toggle, useT } from '@4f/ui';
import { createMemo, createSignal, For, type JSX, Match, Show, Switch } from 'solid-js';
import { createStore } from 'solid-js/store';
import {
  type FormValue,
  initialFormValues,
  invalidFields,
  serializeFormValues,
} from '../directive.ts';
import type { GenerativeUiHandlers } from '../GenerativeUi.tsx';

type FormDirective = Extract<UiDirective, { kind: 'form' }>;

export interface FormRendererProps {
  readonly directive: FormDirective;
  readonly handlers: GenerativeUiHandlers;
}

export function FormRenderer(props: FormRendererProps): JSX.Element {
  const { t } = useT();
  const [values, setValues] = createStore<Record<string, FormValue>>(
    initialFormValues(props.directive.fields),
  );
  const [showErrors, setShowErrors] = createSignal(false);
  const invalid = createMemo(() => invalidFields(props.directive.fields, values));

  const submit = (): void => {
    if (invalid().length > 0) {
      setShowErrors(true);
      return;
    }
    props.handlers.onSubmit(serializeFormValues(props.directive.fields, values));
  };

  return (
    <Form onSubmit={submit} aria-label={props.directive.title ?? t('surface.confirm')}>
      <Show when={props.directive.title}>
        {(title) => <h3 class="f4-genui__title">{title()}</h3>}
      </Show>
      <For each={props.directive.fields}>
        {(field) => (
          <FieldControl
            field={field}
            controlId={`gf-${props.directive.id}-${field.name}`}
            value={values[field.name]}
            invalid={showErrors() && invalid().includes(field.name)}
            onValue={(value) => setValues(field.name, value)}
          />
        )}
      </For>
      <Show when={showErrors() && invalid().length > 0}>
        <p class="f4-genui__error" role="alert">
          {t('surface.formIncomplete')}
        </p>
      </Show>
      <Button type="submit" variant="primary" disabled={props.handlers.disabled}>
        {props.directive.submitLabel}
      </Button>
    </Form>
  );
}

interface FieldControlProps {
  readonly field: UiFormField;
  readonly controlId: string;
  readonly value: FormValue | undefined;
  readonly invalid: boolean;
  readonly onValue: (value: FormValue) => void;
}

/** Map one typed field to its `@4f/ui` control (text/number → TextInput; textarea → Textarea; select; toggle). */
function FieldControl(props: FieldControlProps): JSX.Element {
  const asSelectOptions = (field: UiFormField): SelectOption[] =>
    field.type === 'select' ? field.options.map((option) => ({ ...option })) : [];

  return (
    <Switch>
      <Match when={props.field.type === 'toggle'}>
        <Toggle
          id={props.controlId}
          label={props.field.label}
          checked={Boolean(props.value)}
          onChange={(checked) => props.onValue(checked)}
        />
      </Match>
      <Match when={props.field.type === 'select'}>
        <Select
          id={props.controlId}
          label={props.field.label}
          options={asSelectOptions(props.field)}
          value={String(props.value ?? '')}
          invalid={props.invalid}
          onValue={(value) => props.onValue(value)}
        />
      </Match>
      <Match when={props.field.type === 'number'}>
        <TextInput
          id={props.controlId}
          label={props.field.label}
          value={String(props.value ?? '')}
          inputmode="decimal"
          invalid={props.invalid}
          onValue={(value) => props.onValue(value)}
        />
      </Match>
      <Match when={props.field.type === 'textarea'}>
        <Textarea
          id={props.controlId}
          label={props.field.label}
          value={String(props.value ?? '')}
          invalid={props.invalid}
          rows={4}
          onValue={(value) => props.onValue(value)}
        />
      </Match>
      <Match when={props.field.type === 'text'}>
        <TextInput
          id={props.controlId}
          label={props.field.label}
          value={String(props.value ?? '')}
          invalid={props.invalid}
          onValue={(value) => props.onValue(value)}
        />
      </Match>
    </Switch>
  );
}
